/**
 * Fast lommepenger - ukelønn eller månedslønn, satt opp per barn.
 *
 * Reglene her speiler allowance_next_date() i databasen (migrasjon 0010), som
 * er fasiten: det er den som faktisk fører utbetalingene. Regnestykket finnes
 * likevel her, fordi skjemaet skal kunne si «neste utbetaling 12. aug» i det
 * forelderen velger dag - uten et rundtur til tjeneren for hvert tastetrykk.
 * Demoutgaven av datalaget bruker den samme funksjonen som sin egen fasit.
 *
 * Ukedagene telles som i Postgres sin isodow: mandag = 1, søndag = 7.
 * Månedsdagen stopper på 28, slik at februar aldri hopper over en utbetaling.
 */
import { t } from './i18n'

export const MAX_MONTH_DAY = 28

/** 1-7, mandag først - samme rekkefølge som en norsk kalender. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

/**
 * Navnet på ukedagen. Literaler inni t(), slik at `npm run i18n` finner dem -
 * en tabell med norske navn som oversettes med t(TABELL[dag]) ville aldri
 * blitt plukket opp.
 */
export function weekdayName(day) {
  switch (Number(day)) {
    case 1: return t('Mandag')
    case 2: return t('Tirsdag')
    case 3: return t('Onsdag')
    case 4: return t('Torsdag')
    case 5: return t('Fredag')
    case 6: return t('Lørdag')
    default: return t('Søndag')
  }
}

/** Ukedagen en dato faller på, 1-7 med mandag som 1. */
function isoWeekday(date) {
  return ((date.getDay() + 6) % 7) + 1
}

/**
 * Neste dato lommepengene kommer, strengt etter `after`.
 *
 * Strengt etter er med vilje: setter forelderen opp ukelønn på en mandag,
 * kommer den første mandagen etter. Ellers ville selve oppsettet utbetalt
 * penger, som er en overraskelse ingen ber om.
 *
 * Returnerer null når barnet ikke har fast lommepenger.
 */
export function nextAllowanceDate(period, day, after = new Date()) {
  const from = new Date(after.getFullYear(), after.getMonth(), after.getDate())

  if (period === 'weekly') {
    const wanted = Number(day) || 1
    const delta = ((wanted - isoWeekday(from) + 6) % 7) + 1
    return new Date(from.getFullYear(), from.getMonth(), from.getDate() + delta)
  }

  if (period === 'monthly') {
    const wanted = Math.min(Math.max(Number(day) || 1, 1), MAX_MONTH_DAY)
    const candidate = new Date(from.getFullYear(), from.getMonth(), wanted)
    if (candidate > from) return candidate
    return new Date(from.getFullYear(), from.getMonth() + 1, wanted)
  }

  return null
}

/**
 * Dato -> '2026-08-17', regnet i lokal tid.
 *
 * toISOString() ville gjort det samme i UTC, og i Norge er midnatt lokalt
 * kvelden før i UTC - datoen ville flyttet seg én dag bakover.
 */
export function toIsoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** '2026-08-17' -> Date ved lokal midnatt. */
export function fromIsoDate(iso) {
  const [year, month, day] = String(iso).split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** Har barnet fast lommepenger satt opp? */
export function hasAllowance(child) {
  return Boolean(child)
    && child.allowance_period
    && child.allowance_period !== 'none'
    && Number(child.allowance_ore) > 0
}

/**
 * «Ukelønn 50 kr» - hele beskrivelsen i én tekst med beløpet som plassholder,
 * framfor ordet og beløpet limt sammen hver for seg. Språk plasserer de to
 * ulikt, og en tekst satt sammen av biter kan ikke oversettes godt.
 */
export function allowanceText(child, formatAmount) {
  if (!hasAllowance(child)) return null
  const amount = formatAmount(child.allowance_ore)
  return child.allowance_period === 'weekly'
    ? t('Ukelønn {amount}', { amount })
    : t('Månedslønn {amount}', { amount })
}
